/**
 * Плашка под заголовок в макете frame — измерение по самому PNG-каркасу.
 *
 * Плашка нарисована внутри каркаса, её высота нигде не записана. Оценка по
 * якорю (frameTextAvailableHeight) ошибается: сверка с 41 каркасом дала
 * завышение до +38 px у 13 штук, а у ortalyq.news — на 1027. Поэтому меряем.
 *
 * Ищем сплошную заливку, ВНУТРИ которой стоит якорь текста. Именно так, а не
 * «у нижнего края»: у inform_qz плашка — белая карточка, висящая в воздухе, и
 * поиск снизу её не находил, отчего заголовок раскидывало и выше, и ниже неё.
 *
 * Верхняя граница должна быть резкой. Это отличает нарисованную плашку от
 * градиента: градиент у нижнего края тоже доходит до полной непрозрачности,
 * но входит в неё плавно. Если мерить и его, текст у qara.news и подобных
 * ужмётся в нижнюю треть, хотя весь градиент достаточно тёмный для белого
 * текста. Не нашли плашку — возвращаемся к оценке по якорю.
 *
 * Непрозрачность считаем только по тем колонкам, куда ляжет текст. По всей
 * ширине кадра нельзя: карточка inform_qz отступает от левого края, строка
 * набирала лишь ~92% и плашкой не считалась, хотя под текстом она сплошная.
 */

/** Доля непрозрачных пикселей в строке, при которой строка считается сплошной. */
const SOLID = 0.95;
/** Насколько пустой должна быть строка выше границы, чтобы та считалась резкой. */
const CLEAR = 0.3;
/** На сколько строк выше границы смотрим, отличая плашку от градиента. */
const EDGE_PROBE = 3;
/** Заливка выше этой доли кадра — не плашка, а сплошной фон. */
const MAX_BAND_FRACTION = 0.5;
/** Ширина уменьшенной копии каркаса: строки нам нужны, колонки — почти нет. */
const PROBE_WIDTH = 64;
/**
 * Признаки нарисованной в каркасе графики (логотипа), ниже которой заголовку
 * нельзя. Пороги подобраны по замерам настоящих каркасов:
 *
 *   atyrau.today06 — логотип с лошадьми: альфа 1.00, разброс яркости 254
 *   almaty.today02 — просто градиент с виньеткой: альфа 0.60–0.84, разброс 53–81
 *   taraz.today08  — бледный водяной знак: альфа 0.10–0.25, разброс 88–142
 *
 * Поэтому требуем сразу и почти полную непрозрачность, и высокий разброс:
 * по отдельности каждый признак даёт ложные срабатывания на градиентах.
 */
const CONTENT_CONTRAST = 120;
const CONTENT_MIN_ALPHA = 0.9;
/** Столько строк подряд должны выглядеть графикой — чтобы не ловить шум. */
const CONTENT_RUN = 4;

export interface SolidBand {
  /** Первая строка плашки, render-space px. */
  top: number;
  /** Последняя строка плашки включительно, render-space px. */
  bottom: number;
}

/**
 * Ищет сплошную плашку, содержащую строку anchorY. Чистая функция — на вход
 * доля непрозрачных пикселей по строкам сверху вниз. null = плашки нет.
 */
export function findSolidBandAt(rowOpacity: number[], anchorY: number): SolidBand | null {
  const h = rowOpacity.length;
  if (h === 0) return null;
  const y = Math.max(0, Math.min(h - 1, Math.round(anchorY)));
  if (rowOpacity[y] < SOLID) return null;

  let top = y;
  while (top > 0 && rowOpacity[top - 1] >= SOLID) top--;
  let bottom = y;
  while (bottom < h - 1 && rowOpacity[bottom + 1] >= SOLID) bottom++;

  if (bottom - top + 1 > h * MAX_BAND_FRACTION) return null;
  // Плавный вход сверху — это градиент, а не край плашки.
  if (top < EDGE_PROBE) return null;
  if (rowOpacity[top - EDGE_PROBE] > CLEAR) return null;
  return { top, bottom };
}

interface FrameGrid {
  /** 1 = пиксель непрозрачен. */
  opaque: Uint8Array;
  /** Яркость пикселя 0..255 (для полупрозрачных — как есть). */
  luma: Uint8Array;
  /** Альфа пикселя. */
  alpha: Uint8Array;
}

const gridCache = new Map<string, FrameGrid | null>();

function frameGrid(img: CanvasImageSource, cacheKey: string, canvasHeight: number): FrameGrid | null {
  // Высота — часть ключа: сетка снята под конкретный холст, и тот же каркас,
  // померенный под другую высоту, отдал бы массивы не той длины.
  const key = `${canvasHeight}|${cacheKey}`;
  const cached = gridCache.get(key);
  if (cached !== undefined) return cached;

  let grid: FrameGrid | null = null;
  try {
    const c = document.createElement('canvas');
    c.width = PROBE_WIDTH;
    c.height = canvasHeight;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      ctx.drawImage(img, 0, 0, PROBE_WIDTH, canvasHeight);
      const data = ctx.getImageData(0, 0, PROBE_WIDTH, canvasHeight).data;
      const n = PROBE_WIDTH * canvasHeight;
      grid = { opaque: new Uint8Array(n), luma: new Uint8Array(n), alpha: new Uint8Array(n) };
      for (let i = 0; i < n; i++) {
        const a = data[i * 4 + 3];
        grid.alpha[i] = a;
        grid.opaque[i] = a > 250 ? 1 : 0;
        grid.luma[i] = (data[i * 4] * 299 + data[i * 4 + 1] * 587 + data[i * 4 + 2] * 114) / 1000;
      }
    }
  } catch {
    // Каркас с другого домена без CORS → canvas «протухает» и getImageData
    // кидает. Не беда: молча откатываемся на оценку по якорю.
    grid = null;
  }

  gridCache.set(key, grid);
  return grid;
}

/**
 * Первая строка ниже якоря, где в каркасе что-то нарисовано — логотип, подпись.
 * Заголовок не должен на неё наезжать.
 *
 * Нужно для каркасов вроде atyrau.today06: там сплошная чёрная плашка начинается
 * ниже якоря, поэтому поиском «плашка вокруг якоря» она не ловится, а внутри неё
 * снизу стоит логотип с лошадьми. Длинный заголовок доезжал прямо до него.
 */
export function findContentTopBelow(
  contrast: number[], alpha: number[], fromY: number,
): number | null {
  const isContent = (y: number) => alpha[y] >= CONTENT_MIN_ALPHA && contrast[y] >= CONTENT_CONTRAST;
  const start = Math.max(0, Math.round(fromY));
  for (let y = start; y + CONTENT_RUN <= contrast.length; y++) {
    let run = 0;
    while (run < CONTENT_RUN && isContent(y + run)) run++;
    if (run === CONTENT_RUN) return y;
  }
  return null;
}

export interface FrameTextSpace {
  /** Сплошная плашка, внутри которой стоит якорь. */
  band: SolidBand | null;
  /** Первая строка ниже якоря с нарисованной графикой (логотип, подпись). */
  contentTop: number | null;
}

/**
 * Что каркас позволяет заголовку: плашку вокруг якоря и первую графику под ним.
 * xFrom/xTo — доли ширины кадра, занятые текстом: только их и смотрим.
 */
export function measureFrameTextSpace(
  img: CanvasImageSource,
  cacheKey: string,
  canvasHeight: number,
  anchorY: number,
  xFrom = 0,
  xTo = 1,
): FrameTextSpace {
  const grid = frameGrid(img, cacheKey, canvasHeight);
  if (!grid) return { band: null, contentTop: null };

  const lo = Math.max(0, Math.min(PROBE_WIDTH - 1, Math.floor(xFrom * PROBE_WIDTH)));
  const hi = Math.max(lo + 1, Math.min(PROBE_WIDTH, Math.ceil(xTo * PROBE_WIDTH)));
  const width = hi - lo;

  const opacity: number[] = new Array(canvasHeight);
  const contrast: number[] = new Array(canvasHeight);
  const meanAlpha: number[] = new Array(canvasHeight);

  for (let y = 0; y < canvasHeight; y++) {
    let opaque = 0, aSum = 0, seen = 0, min = 255, max = 0;
    for (let x = lo; x < hi; x++) {
      const i = y * PROBE_WIDTH + x;
      opaque += grid.opaque[i];
      aSum += grid.alpha[i];
      // Прозрачные пиксели цвета не несут — в разброс их не берём.
      if (grid.alpha[i] > 128) {
        seen++;
        const l = grid.luma[i];
        if (l < min) min = l;
        if (l > max) max = l;
      }
    }
    opacity[y] = opaque / width;
    meanAlpha[y] = aSum / width / 255;
    contrast[y] = seen > 0 ? max - min : 0;
  }

  return {
    band: findSolidBandAt(opacity, anchorY),
    contentTop: findContentTopBelow(contrast, meanAlpha, anchorY),
  };
}

/** Сплошная заливка строки каркаса: границы и цвет. Для дорисовки плашки вверх. */
export interface BandFill {
  /** Левый край заливки, render-space px. */
  x0: number;
  /** Правый край заливки (исключительно), render-space px. */
  x1: number;
  /** Средний цвет заливки. */
  color: string;
}

/** Колонок в пробе строки: шаг ~5 px при кадре 1080 — края ловятся достаточно точно. */
const FILL_PROBE_COLS = 216;
/** Максимальный разброс канала внутри заливки. Больше — это градиент/картинка, не плашка. */
const FILL_MAX_DEVIATION = 14;

/**
 * Ищет в строке RGBA-пробы сплошную одноцветную заливку. Чистая функция.
 * null — строка не похожа на однотонную плашку (растить её цветом нельзя).
 */
export function bandFillFromRow(rgba: Uint8ClampedArray, cols: number, canvasWidth: number): BandFill | null {
  // Самый длинный непрерывный прогон непрозрачных колонок.
  let bestStart = -1; let bestLen = 0; let curStart = -1;
  for (let i = 0; i <= cols; i++) {
    const opaque = i < cols && rgba[i * 4 + 3] > 250;
    if (opaque && curStart < 0) curStart = i;
    if (!opaque && curStart >= 0) {
      if (i - curStart > bestLen) { bestLen = i - curStart; bestStart = curStart; }
      curStart = -1;
    }
  }
  // Плашка короче трети кадра — не плашка.
  if (bestLen < cols / 3) return null;

  let r = 0; let g = 0; let b = 0;
  for (let i = bestStart; i < bestStart + bestLen; i++) {
    r += rgba[i * 4]; g += rgba[i * 4 + 1]; b += rgba[i * 4 + 2];
  }
  r = Math.round(r / bestLen); g = Math.round(g / bestLen); b = Math.round(b / bestLen);
  for (let i = bestStart; i < bestStart + bestLen; i++) {
    if (Math.abs(rgba[i * 4] - r) > FILL_MAX_DEVIATION ||
        Math.abs(rgba[i * 4 + 1] - g) > FILL_MAX_DEVIATION ||
        Math.abs(rgba[i * 4 + 2] - b) > FILL_MAX_DEVIATION) return null;
  }

  const px = canvasWidth / cols;
  // Заливка до края пробы — прижимаем к краю кадра, чтобы не оставить щель.
  const x0 = bestStart === 0 ? 0 : Math.round(bestStart * px);
  const end = bestStart + bestLen;
  const x1 = end === cols ? canvasWidth : Math.round(end * px);
  return { x0, x1, color: `rgb(${r}, ${g}, ${b})` };
}

const fillCache = new Map<string, BandFill | null>();

/**
 * Заливка каркаса в строке y (render-space px холста canvasHeight).
 * null — строка неоднотонная или каркас нечитаем (CORS).
 */
export function measureBandFill(
  img: CanvasImageSource, cacheKey: string, canvasHeight: number, y: number,
): BandFill | null {
  const key = `${canvasHeight}|${Math.round(y)}|${cacheKey}`;
  const cached = fillCache.get(key);
  if (cached !== undefined) return cached;

  let fill: BandFill | null = null;
  try {
    const c = document.createElement('canvas');
    c.width = FILL_PROBE_COLS;
    c.height = canvasHeight;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      ctx.drawImage(img, 0, 0, FILL_PROBE_COLS, canvasHeight);
      const row = ctx.getImageData(0, Math.max(0, Math.min(canvasHeight - 1, Math.round(y))), FILL_PROBE_COLS, 1).data;
      fill = bandFillFromRow(row, FILL_PROBE_COLS, 1080);
    }
  } catch {
    fill = null; // протухший canvas без CORS — молча без дорисовки
  }

  fillCache.set(key, fill);
  return fill;
}
